import React, { useState, useEffect, useRef } from 'react';
import { Globe, Home, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

interface FlightCountrySelectorProps {
  country: string | null;
  countryNames: string[];
  isLastFlight?: boolean;
  onCountryChange: (country: string) => Promise<void>;
}

// Map country names to their flag emoji
const getCountryFlag = (countryName: string): string => {
  const countryFlagMap: Record<string, string> = {
    'Switzerland': '🇨🇭',
    'Israel': '🇮🇱',
    'United States': '🇺🇸',
    'United Kingdom': '🇬🇧',
    'France': '🇫🇷',
    'Germany': '🇩🇪',
    'Italy': '🇮🇹',
    'Spain': '🇪🇸',
    'Netherlands': '🇳🇱',
    'Belgium': '🇧🇪',
    'Austria': '🇦🇹',
    'Greece': '🇬🇷',
    'Portugal': '🇵🇹',
    'Poland': '🇵🇱',
    'Cyprus': '🇨🇾',
    'Czech Republic': '🇨🇿',
    'Hungary': '🇭🇺',
    'Sweden': '🇸🇪',
    'Norway': '🇳🇴',
    'Denmark': '🇩🇰',
    'Finland': '🇫🇮',
    'Ireland': '🇮🇪',
    'Canada': '🇨🇦',
    'Australia': '🇦🇺',
    'Japan': '🇯🇵',
    'China': '🇨🇳',
    'South Korea': '🇰🇷',
    'India': '🇮🇳',
    'Brazil': '🇧🇷',
    'Argentina': '🇦🇷',
    'Mexico': '🇲🇽',
    'South Africa': '🇿🇦',
    'Turkey': '🇹🇷',
    'Russia': '🇷🇺',
    'Thailand': '🇹🇭',
    'Singapore': '🇸🇬',
    'Malaysia': '🇲🇾',
    'Indonesia': '🇮🇩',
    'Philippines': '🇵🇭',
    'Vietnam': '🇻🇳',
    'Egypt': '🇪🇬',
    'UAE': '🇦🇪',
    'United Arab Emirates': '🇦🇪',
    'Saudi Arabia': '🇸🇦',
    'New Zealand': '🇳🇿',
    'Chile': '🇨🇱',
    'Colombia': '🇨🇴',
    'Peru': '🇵🇪',
  };

  return countryFlagMap[countryName] || '🌍';
};

export const FlightCountrySelector: React.FC<FlightCountrySelectorProps> = ({
  country,
  countryNames,
  isLastFlight = false,
  onCountryChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter countries based on search query
  const filteredCountries = countryNames.filter((countryName) =>
    countryName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Focus search input when dialog opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure dialog is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset search when closing
  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  // Handle country selection
  const handleSelect = async (countryName: string) => {
    await onCountryChange(countryName);
    handleClose();
  };

  // If it's the last flight and country is not set, show home icon
  if (isLastFlight && !country) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 min-w-[140px]">
        <div className="flex flex-col items-center gap-1 p-2">
          <Home className="h-10 w-10 text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="flex flex-col items-center justify-center gap-2 min-w-[140px] group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <button
          onClick={() => setIsOpen(true)}
          className="relative flex flex-col items-center gap-1 p-2 rounded-md hover:bg-accent/50 transition-colors cursor-pointer border-none bg-transparent"
        >
          {country ? (
            <>
              <span className="text-4xl" role="img" aria-label={`${country} flag`}>
                {getCountryFlag(country)}
              </span>
              <div className="flex items-center gap-1">
                <span className="text-sm font-medium text-center">
                  {country}
                </span>
                <ChevronDown
                  className={`h-3 w-3 text-muted-foreground transition-opacity ${
                    isHovered ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              </div>
            </>
          ) : (
            <>
              <Globe className="h-10 w-10 text-muted-foreground" />
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">
                  Unknown
                </span>
                <ChevronDown
                  className={`h-3 w-3 text-muted-foreground transition-opacity ${
                    isHovered ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              </div>
            </>
          )}
        </button>
      </div>

      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Select Country</h3>

            <Input
              ref={searchInputRef}
              placeholder="Search countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />

            <div className="max-h-[300px] overflow-y-auto space-y-1">
              {filteredCountries.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No countries found
                </div>
              ) : (
                filteredCountries.map((countryName) => (
                  <button
                    key={countryName}
                    onClick={() => handleSelect(countryName)}
                    className="w-full flex items-center gap-3 p-3 rounded-md hover:bg-accent transition-colors text-left"
                  >
                    <span className="text-2xl" role="img" aria-label={`${countryName} flag`}>
                      {getCountryFlag(countryName)}
                    </span>
                    <span className="font-medium">{countryName}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
